import React from 'react';
import { Lock } from 'lucide-react';

/**
 * Dashboard v2 pipeline band per `Dashboard v2.dc.html` — proportional stage
 * bar on the single-hue blue ramp (pipeline-1..5, never rainbow), legend with
 * mono counts, locked-stages note, and the awarded-this-month tally on the
 * right. Presentational: five stages arrive in ramp order with real counts.
 */
export interface PipelineStageDatum {
  label: string;
  count: number;
}

export interface PipelineBandProps {
  /** The five active stages in ramp order (Upload BOM → Quotes & Proposals). */
  stages: PipelineStageDatum[];
  awardedThisMonth: number;
  className?: string;
}

const RAMP = ['bg-pipeline-1', 'bg-pipeline-2', 'bg-pipeline-3', 'bg-pipeline-4', 'bg-pipeline-5'];

export const PipelineBand: React.FC<PipelineBandProps> = ({ stages, awardedThisMonth, className = '' }) => {
  const total = stages.reduce((sum, stage) => sum + stage.count, 0);

  return (
    <section className={`rounded-card border border-line bg-surface px-s6 py-[15px] max-md:px-s5 ${className}`}>
      <div className="mb-[11px] flex items-baseline justify-between gap-s3">
        <span className="text-[14px] font-semibold text-ink">Pipeline</span>
        <span className="flex items-center gap-[5px] text-body-sm text-ink-muted max-md:hidden">
          <Lock className="h-[11px] w-[11px] shrink-0" />
          Financing → Contracting coming soon
        </span>
      </div>
      <div className="mb-s4 flex h-s3 overflow-hidden rounded-round bg-line-divider-soft">
        {total > 0 &&
          stages.map(
            (stage, i) =>
              stage.count > 0 && (
                <div
                  key={stage.label}
                  className={RAMP[i]}
                  style={{ width: `${(stage.count / total) * 100}%` }}
                  title={`${stage.label}: ${stage.count}`}
                />
              ),
          )}
      </div>
      <div className="flex flex-wrap items-center gap-x-[22px] gap-y-s2">
        {stages.map((stage, i) => (
          <div key={stage.label} className="flex items-center gap-[7px] text-[12.5px] text-ink-body">
            <span className={`h-s3 w-s3 shrink-0 rounded-[2px] ${RAMP[i]}`} />
            {stage.label} <span className="font-mono font-semibold">{stage.count}</span>
          </div>
        ))}
        <div className="ml-auto text-[12.5px] text-ink-secondary max-md:ml-0 max-md:basis-full">
          {awardedThisMonth > 0
            ? `${awardedThisMonth} awarded this month`
            : 'None awarded this month'}
        </div>
      </div>
    </section>
  );
};

export default PipelineBand;
